/**
 * Die Gestaltung des Bretts — **beschlossen**, nicht mehr vorläufig.
 *
 * **Entschieden am 2026-10-05 vom User**, aus drei vorgelegten Varianten: **Werkstatt**, dazu
 * **Symbole** auf den Figuren und eine **farbige Startzone**. Damit sind die Punkte abgearbeitet,
 * die `initPrompt.md` (Ergänzungsblock) ausdrücklich der Abstimmung vorbehalten hat: Farbwerte,
 * Sektoren, Startbereiche, Ring, Zielfeld, Hexfeld-Ränder, Figurenstil und die Zustände.
 *
 * **Der Leitgedanke der Variante: Farbe am Rand, Ruhe in der Mitte.** Die Sektorfarbe steht
 * dort, wo sie etwas aussagt — in der eigenen Startzone (§10). Nach innen wird das Brett fast
 * neutral, und die Figuren sind das einzige Gesättigte darauf. Das ist keine Geschmacksfrage,
 * sondern eine Rechnung: auf dem Brett stehen bis zu **54 Figuren in sechs Farben**, dazu bis zu
 * fünfundzwanzig markierte Felder. Trüge auch die Fläche kräftige Farbe, müsste jede
 * Hervorhebung dagegen anschreien.
 *
 * **Die Figurenfarbe ist nicht die Feldfarbe**, sondern das dunklere Glied derselben Familie.
 * Der häufigste Fall überhaupt ist eine Figur auf dem eigenen Sektor — jeder beginnt dort —, und
 * mit gleicher Farbe verschwände sie darauf.
 *
 * **Woher die Farbidentitäten kommen:** aus `docs/board-reference.svg`, der für Geometrie *und*
 * Farben verbindlichen Vorlage (`docs/GAME_RULES.md`). Grün bleibt Grün. Geändert sind die
 * **Werte**, nicht die Zuordnung: die Vorlage ist für Papier gezeichnet, diese Werte sind für
 * einen Bildschirm gesetzt.
 *
 * **Was hier bewusst fehlt:** welche Felder zur Startzone gehören. Das rechnet die Engine aus
 * der RuleConfig (entschieden: zwei Reihen, Nr. 11) — die Vorlage markiert drei. Eine Farbdatei
 * entscheidet nichts über das Brett (`CLAUDE.md` Regel 9).
 */

import type { SectorId } from '@flaghex/engine';

export interface CellColors {
  readonly fill: string;
  readonly stroke: string;
}

/**
 * Ein Sektor führt fünf Werte, und jeder hat eine eigene Aufgabe.
 *
 * `fill` ist die Fläche im Brettinneren — fast neutral, erkennbar getönt. `start` ist dieselbe
 * Familie in kräftig, für die Startzone. `stroke` ist der Feldrand **in der Startzone**; im
 * Inneren führt das Gitter `GRID_COLOR`. `piece` ist die Figur, `symbol` das Zeichen darauf.
 *
 * **Warum `symbol` je Sektor und nicht einmal für alle** (entschieden am 2026-10-05 vom User,
 * `ARCHITECTURE.md` **E34**): mit einem durchgehend weißen Symbol muss jede Figurenfarbe dunkel
 * genug dafür sein — und Gelb wird dabei olivbraun, bevor es reicht. Das ist der Punkt, an dem
 * eine Regel über sechs Farben eine der sechs beschädigt. Gelb und Orange tragen deshalb ein
 * dunkles Symbol auf hellerem Grund; die Aussage bleibt dieselbe, der Weg dorthin ist je Farbe
 * ein anderer.
 */
export interface SectorColors extends CellColors {
  readonly start: string;
  readonly piece: string;
  readonly symbol: string;
}

/** Die sechs Spielerfarben. Die Identitäten stehen fest, die Werte sind beschlossen. */
export const SECTOR_COLORS: Readonly<Record<SectorId, SectorColors>> = {
  green: { fill: '#eff6f1', start: '#cfe9d5', stroke: '#6aab79', piece: '#2f9348', symbol: '#ffffff' },
  yellow: { fill: '#faf7ec', start: '#f3e9bd', stroke: '#c3ab4d', piece: '#dfb524', symbol: '#41330a' },
  red: { fill: '#fbf1f1', start: '#f2d6d6', stroke: '#cf8b8b', piece: '#bf4747', symbol: '#ffffff' },
  blue: { fill: '#eef3f8', start: '#d5e3f1', stroke: '#7ea4cb', piece: '#3b77b8', symbol: '#ffffff' },
  orange: { fill: '#fbf4ec', start: '#f4e0c7', stroke: '#cc9a5c', piece: '#e08a33', symbol: '#45270a' },
  purple: { fill: '#f5f1fa', start: '#e2d3ef', stroke: '#a383c4', piece: '#854bbf', symbol: '#ffffff' },
};

/**
 * Das Gitter im Brettinneren — **ein** neutraler Ton für alle Felder.
 *
 * 217 farbige Feldränder ergeben ein Muster, das niemand liest und das mit jeder Markierung
 * konkurriert. Farbe führt der Rand nur in der Startzone.
 */
export const GRID_COLOR = '#d9dee3';

/**
 * Der Ring ist grau und gehört keinem Sektor (§7) — und trägt eine **zweite Kontur**.
 *
 * Grau allein ist neben sechs hellen Sektoren schwach, und sobald eine Figur darauf steht, ist
 * die Fläche halb verdeckt. Die gestrichelte Innenkontur bleibt auch dann sichtbar. Der Ring ist
 * Spielstruktur und keine Schattierung: auf ihm bewegt man sich wie auf einer Geraden (§7).
 */
// Die Werte sind bewusst **fast** neutral: ein Hauch Kühle statt totem Grau, aber weit genug
// von Blau entfernt, dass niemand den Ring für einen Sektor hält (§7: Grau ist keine
// Spielerfarbe). Der Test `board-design` hält diese Grenze fest.
export const RING_COLORS: CellColors = { fill: '#e8eaec', stroke: '#a3a7ab' };
export const RING_INNER_RATIO = 0.78;
export const RING_INNER_STROKE_RATIO = 0.045;
export const RING_DASH_RATIO = { dash: 0.17, gap: 0.13 } as const;

/**
 * Das zentrale Zielfeld — die einzige Stelle des Bretts, die laut sein darf.
 *
 * Dort entscheidet sich die Partie (§19, §23), und es ist das einzige Feld mit einer eigenen
 * Regel für jede Figurenklasse. Der helle Innenring hebt es zusätzlich heraus, ohne die
 * Beschriftung zu stören.
 */
export const GOAL_COLORS: CellColors = { fill: '#c0392b', stroke: '#7e241a' };
export const GOAL_INNER_RATIO = 0.62;
export const GOAL_INNER_STROKE_RATIO = 0.055;

/** Die Beschriftung des Zielfeldes steht auf kräftigem Rot und braucht deshalb Weiß. */
export const GOAL_LABEL_COLOR = '#ffffff';
export const GOAL_LABEL_FONT_RATIO = 0.39;

/** Die Auswahl eines Feldes: ein kräftiger Rand, keine Füllung — die Fläche gehört dem Sektor. */
export const SELECTION_COLOR = '#15191d';

/** Linienbreiten im Verhältnis zur Feldgröße, damit jede Brettgröße gleich aussieht. */
export const STROKE_RATIO = { cell: 0.045, goal: 0.07, selection: 0.115 } as const;

/** Schriftgröße der Feldkoordinaten, ebenfalls relativ zur Feldgröße. */
export const COORDINATE_FONT_RATIO = 0.46;
export const COORDINATE_COLOR = '#7b858f';

/**
 * Die Figuren: eine Scheibe mit dunklem Rand und einem **Symbol** (beschlossen am 2026-10-05).
 *
 * Nah an einer physischen Spielfigur, trägt jedes Symbol, und bleibt bei dreißig Bildpunkten
 * noch rund. Das Symbol ist sprachunabhängig und schneller zu erfassen als ein Kürzel — „Lä" und
 * „St" muss man lernen, einen Doppelpfeil nicht. Die Formen stehen in `symbols.ts`.
 */
export const PIECE_RADIUS_RATIO = 0.62;
export const PIECE_STROKE_RATIO = 0.075;
export const PIECE_SYMBOL_RATIO = 0.48;
export const PIECE_STROKE_COLOR = '#1f2428';

/**
 * Die **Rückseite** einer Scheibe — eine verdeckte Figur in der Aufstellung (`R17`, Nr. 55).
 *
 * Wie die Anleitung sie zeichnet (Entscheidung des Users, 2026-10-15: „so wie in der
 * Anleitung“): dieselbe Scheibe in der Figurenfarbe mit Rand, darin ein feiner, blasser Innenring,
 * **kein Symbol**. Der Ring sagt „Rückseite“, ohne an eine Figur zu erinnern. `ratio` bezieht sich
 * auf den Figurenradius, `stroke` auf die Feldgröße. Spiel und Anleitung lesen dieselben Werte.
 */
export const COVERED_RING = { ratio: 0.62, stroke: 0.04, opacity: 0.35 } as const;

/**
 * Die Deckkraft einer erwogenen Figur (§36) — zusammen mit einer gestrichelten Kontur.
 *
 * **Zwei Signale, nicht eines.** Deckkraft allein fällt auf hellen Feldern kaum auf, und §63
 * verlangt ausdrücklich, dass unterscheidbar bleibt, wo die Figur steht und wohin sie zöge.
 */
export const GHOST_OPACITY = 0.45;
export const GHOST_DASH_RATIO = { dash: 0.14, gap: 0.1 } as const;

/** Die Spur vom Ausgangs- zum Zielfeld einer Erwägung — sie macht das Paar lesbar. */
export const TRACE_COLOR = SELECTION_COLOR;
export const TRACE_STROKE_RATIO = 0.07;
export const TRACE_DASH_RATIO = { dash: 0.18, gap: 0.14 } as const;
export const TRACE_OPACITY = 0.5;

/**
 * Eine Figur, die diese Aktion vom Brett nimmt — Opfer eines Schlags oder Randwurf.
 *
 * **Ein Kranz um die Figur, nicht ihr Rand** (entschieden am 2026-10-05 vom User,
 * `ARCHITECTURE.md` **E35**). Vorher war die Markierung der Figurenrand selbst — und lag damit
 * genau dort, wo bei einem Schlag der Schatten des Angreifers liegt: drei Lagen auf einem Feld,
 * von denen die mittlere verdeckt war. Der Kranz liegt weiter außen als jede Figur und bleibt
 * deshalb sichtbar, egal was darüber steht.
 *
 * **Ein Kreis, kein Sechseck** — Befund der Sichtprüfung zu D2a. Als Sechseck war der Kranz von
 * der Schlag*markierung* (§50) kaum zu unterscheiden: beide rot, beide sechseckig, verschieden
 * nur in Radius und Strichstärke. Die Trennung folgt E33 („die Form trägt die Aussage"): ein
 * Sechseck gehört dem **Feld** („hier ist etwas möglich"), ein Kreis der **Figur** („diese
 * fällt"). Beide können gleichzeitig auf dem Brett stehen, sobald eine Figur gewählt und ein
 * Zug erwogen ist — dann muss der Unterschied ohne Nachmessen lesbar sein.
 *
 * Der Radius liegt zwischen `PIECE_RADIUS_RATIO` (sonst verdeckt ihn der Schatten) und dem
 * **Inkreis** eines Feldes, √3⁄2 (sonst ragt er ins Nachbarfeld). Zwei Tests halten beide
 * Grenzen fest.
 */
export const DOOMED_COLOR = '#c0392b';
export const DOOMED_STROKE_RATIO = 0.11;
export const DOOMED_RING_RATIO = 0.78;

/**
 * Was auf einem Feld möglich ist — **die Form trägt die Aussage, die Farbe verstärkt sie**.
 *
 * §50 verlangt Bewegung, Schlag und Stoß *unterscheidbar*. Mit Farbe allein wäre das für
 * jemanden mit Rot-Grün-Schwäche nicht erfüllt, und auf einem Brett mit sechs Spielerfarben
 * konkurrierte jede weitere Farbe mit den Figuren. Deshalb:
 *
 * - **Bewegung** — ein Punkt in der Feldmitte. Das Zielfeld ist leer, der Punkt verdeckt nichts
 *   und liest sich auch bei fünfundzwanzig Zielen auf einen Blick.
 * - **Schlag** — ein Ring am Feldrand. Dort steht eine Figur; ein Punkt läge unter ihr.
 * - **Stoß** — ein Pfeil. Der Stoß ist die einzige Aktion mit einer **Richtung** (§18); ohne sie
 *   bliebe offen, wohin die Figur weicht.
 * - **Respawn** und **Aufstellen** — eine gestrichelte Kontur: ein Feld, das befüllt werden darf.
 *   Dieselbe Form, verschiedene Farbe, weil das eine ein Zug ist (§22) und das andere nicht (§10).
 */
export const HIGHLIGHT_COLORS = {
  MOVE: '#2b3137',
  CAPTURE: '#c0392b',
  PUSH: '#a06800',
  RESPAWN: '#1a5fb4',
  PLACE: '#2b3137',
} as const;

export type HighlightKind = keyof typeof HIGHLIGHT_COLORS;

/** Die Formen der Hervorhebungen, relativ zur Feldgröße. */
export const HIGHLIGHT_SHAPE = {
  moveDot: 0.23,
  captureRing: 0.84,
  captureStroke: 0.15,
  /**
   * Der Stoßpfeil (`R16`): Er beginnt `arrowStart` vom Mittelpunkt des Opfers, knapp innerhalb
   * des Figurenrands (`PIECE_RADIUS_RATIO`), damit er aus der Figur heraus zu kommen scheint,
   * und endet `arrowInset` vor dem Mittelpunkt des Landefelds. Dieselben Maße wie in der
   * Anleitung (`apps/web/manual/render.tsx`).
   */
  arrowStart: 0.55,
  arrowInset: 0.22,
  arrowHead: 0.3,
  arrowStroke: 0.15,
  placeRing: 0.84,
  placeStroke: 0.11,
  placeDash: 0.22,
  placeGap: 0.16,
} as const;
